import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Crown, Check, Loader2, School, Users, Trophy, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const subscriptionId = searchParams.get("subscription_id");
    if (subscriptionId) {
      verifySubscription(subscriptionId);
    } else {
      setVerifying(false);
    }
  }, [searchParams]);

  const verifySubscription = async (subscriptionId) => {
    try {
      const response = await axios.post(
        `${API}/subscription/verify/${subscriptionId}`,
        {},
        { headers: getAuthHeaders(token) }
      );
      
      if (response.data.status === "active") {
        setSuccess(true);
        await refreshUser();
        toast.success("Premium subscription activated!");
      }
    } catch (error) {
      toast.error("Failed to verify subscription");
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
        <Card className="card-base max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#4F46E5] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Verifying Subscription...
            </h2>
            <p className="text-slate-500">Please wait while we activate your premium account</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient flex items-center justify-center">
      <Card className="card-base max-w-lg w-full mx-4 overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-yellow-400 to-orange-500" />
        <CardContent className="p-8">
          {success ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Welcome to Premium!
              </h2>
              <p className="text-slate-600 mb-6">
                Your premium subscription is now active. You can now create classrooms and share tests with your students.
              </p>
              
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-slate-800 mb-3">What's included:</h4>
                <div className="space-y-2 text-left">
                  <div className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Unlimited classrooms</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Up to 50 students per classroom</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Share tests with students</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-700">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Track scores and leaderboards</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/dashboard")}
                  className="flex-1"
                >
                  Go to Dashboard
                </Button>
                <Button 
                  onClick={() => navigate("/classrooms")}
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white gap-2"
                >
                  <School className="w-4 h-4" />
                  Create Classroom
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-slate-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Subscription Pending
              </h2>
              <p className="text-slate-600 mb-6">
                We couldn't verify your subscription. If you completed the payment, please try refreshing or contact support.
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/pricing")}
                  className="flex-1"
                >
                  Back to Pricing
                </Button>
                <Button 
                  onClick={() => window.location.reload()}
                  className="flex-1 btn-primary"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionSuccess;
